import React, { useState, useEffect, useRef } from 'react';
import './FitnessTestWidget.css';

// Video mappings for fitness test demonstrations
const testVideos = {
    pushup: {
        primary: "/videos/exercises/pushups.mp4",
        modification: "/videos/exercises/kneepushups.mp4",
        modificationName: "Knee Push-Ups"
    },
    pullup: {
        primary: "/videos/exercises/pullups.mp4",
        modification: "/videos/exercises/australianpullups.mp4",
        modificationName: "Australian Pull-Ups"
    },
    situp: {
        primary: "/videos/exercises/crunches.mp4",
        modification: "/videos/exercises/crunches2.mp4",
        modificationName: "Basic Crunches"
    },
    squat: {
        primary: "/videos/exercises/squats.mp4",
        modification: "/videos/exercises/airsquats.mp4",
        modificationName: "Air Squats"
    },
    balance: {
        primary: "/videos/exercises/wallsit.mp4", // Using wall sit as closest balance exercise
        modification: null,
        modificationName: null
    },
    endurance: {
        primary: "/videos/exercises/jumpingjacks.mp4",
        modification: "/videos/exercises/highknees.mp4",
        modificationName: "High Knees"
    },
    benchpress: {
        primary: "/videos/exercises/benchpress.mp4",
        modification: "/videos/exercises/pushups.mp4",
        modificationName: "Push-Ups (bodyweight alternative)"
    },
    squatlift: {
        primary: "/videos/exercises/squats.mp4", // Using regular squats as reference
        modification: "/videos/exercises/dumbbellsquats.mp4",
        modificationName: "Dumbbell Squats"
    },
    deadlift: {
        primary: "/videos/exercises/deadlift.mp4",
        modification: "/videos/exercises/dumbbellrows.mp4",
        modificationName: "Dumbbell Rows"
    },
    bentrow: {
        primary: "/videos/exercises/bentoverrows.mp4",
        modification: "/videos/exercises/dumbbellrows.mp4",
        modificationName: "Dumbbell Rows"
    }
};

const FitnessTestWidget = ({ testType, onComplete, onCancel }) => {
    const [phase, setPhase] = useState('setup'); // setup, instructions, countdown, testing, results
    const [userAge, setUserAge] = useState('');
    const [userGender, setUserGender] = useState('');
    const [userWeight, setUserWeight] = useState(''); // For weight-based tests
    const [countdown, setCountdown] = useState(5);
    const [timeLeft, setTimeLeft] = useState(60);
    const [reps, setReps] = useState(0);
    const [weight, setWeight] = useState(0); // For weight-based tests
    const [isActive, setIsActive] = useState(false);
    const [showModification, setShowModification] = useState(false);
    const videoRef = useRef(null);
    const modVideoRef = useRef(null);

    const testConfigs = {
        pushup: {
            name: 'Push-Up Test',
            duration: 60,
            isWeightBased: false,
            instructions: [
                'Lie face down with hands placed slightly wider than shoulders',
                'Keep your body straight from head to heels',
                'Lower your chest to within 2 inches of the floor',
                'Push back up to starting position',
                'Count each complete repetition',
                'Stop when you can no longer maintain proper form'
            ],
            tips: 'Focus on form over speed. Quality repetitions count more than quantity!'
        },
        pullup: {
            name: 'Pull-Up Test',
            duration: 180,
            isWeightBased: false,
            instructions: [
                'Hang from a pull-up bar with palms facing away',
                'Arms should be fully extended at the start',
                'Pull yourself up until your chin clears the bar',
                'Lower yourself back to full arm extension',
                'Count each complete repetition',
                'Rest as needed but stay hanging if possible'
            ],
            tips: 'If you can\'t do pull-ups yet, that\'s okay! Record 0 and work on assisted pull-ups.'
        },
        situp: {
            name: 'Sit-Up Test',
            duration: 60,
            isWeightBased: false,
            instructions: [
                'Lie on your back with knees bent at 90 degrees',
                'Place hands behind your head, fingers interlaced',
                'Sit up until your elbows touch your knees',
                'Lower back down until shoulder blades touch floor',
                'Count each complete repetition',
                'Maintain steady rhythm throughout'
            ],
            tips: 'Don\'t pull on your neck! Use your core muscles to lift your torso.'
        },
        squat: {
            name: 'Squat Test',
            duration: 120,
            isWeightBased: false,
            instructions: [
                'Stand with feet shoulder-width apart',
                'Lower your body until thighs are parallel to floor',
                'Keep your chest up and knees behind toes',
                'Return to standing position',
                'Count each complete repetition',
                'Maintain proper form throughout'
            ],
            tips: 'Pretend you\'re sitting back into a chair. Don\'t let your knees cave inward!'
        },
        benchpress: {
            name: 'Bench Press Test',
            duration: 300, // 5 minutes max
            isWeightBased: true,
            instructions: [
                'Lie flat on bench with feet firmly on the ground',
                'Grip the bar with hands slightly wider than shoulders',
                'Lower the bar to your chest with control',
                'Press the bar back up to full arm extension',
                'Find your 1-rep max or test a specific weight',
                'Have a spotter for safety'
            ],
            tips: 'Warm up thoroughly! Start light and work up to your max safely.'
        },
        squatlift: {
            name: 'Squat Lift Test',
            duration: 300,
            isWeightBased: true,
            instructions: [
                'Position bar on your upper back/traps',
                'Stand with feet shoulder-width apart',
                'Lower down until thighs are parallel to floor',
                'Keep chest up and knees tracking over toes',
                'Drive through heels to return to standing',
                'Test your 1-rep max or specific weight'
            ],
            tips: 'Keep your core tight and maintain proper form. Don\'t sacrifice form for weight!'
        },
        deadlift: {
            name: 'Deadlift Test',
            duration: 300,
            isWeightBased: true,
            instructions: [
                'Stand with feet hip-width apart, bar over mid-foot',
                'Bend at hips and knees to grip the bar',
                'Keep your back straight and chest up',
                'Drive through heels and hips to lift the bar',
                'Stand tall with shoulders back at the top',
                'Lower the bar back down with control'
            ],
            tips: 'The deadlift is king of exercises! Focus on perfect form over heavy weight.'
        },
        bentrow: {
            name: 'Bent-Over Row Test',
            duration: 300,
            isWeightBased: true,
            instructions: [
                'Hold barbell with overhand grip, hands shoulder-width apart',
                'Bend forward at hips, keeping back straight',
                'Let the bar hang at arm\'s length',
                'Pull the bar to your lower chest/upper abdomen',
                'Squeeze shoulder blades together at the top',
                'Lower with control and repeat'
            ],
            tips: 'Keep your core engaged and avoid using momentum. Feel it in your lats and rhomboids!'
        },
        balance: {
            name: 'Balance Test',
            duration: 60,
            isWeightBased: false,
            instructions: [
                'Stand on one foot with your eyes closed',
                'Place hands on your hips',
                'Lift your other foot off the ground',
                'Hold this position for as long as possible',
                'Switch feet if you want to test both sides',
                'Timer stops when you touch down or open eyes'
            ],
            tips: 'Focus on a point when eyes are open, engage your core for stability!'
        },
        endurance: {
            name: 'Endurance Test',
            duration: 600, // 10 minutes max
            isWeightBased: false,
            instructions: [
                'This is a cardiovascular endurance assessment',
                'Choose: running, cycling, rowing, or step-ups',
                'Maintain steady effort for the duration',
                'Rate your perceived exertion on a 1-10 scale',
                'Monitor your heart rate if possible',
                'Cool down properly when finished'
            ],
            tips: 'Pace yourself! This tests your aerobic capacity and mental toughness.'
        }
    };

    const intervalRef = useRef(null);
    const countdownRef = useRef(null);

    const currentTest = testConfigs[testType] || testConfigs.pushup;
    const currentVideos = testVideos[testType] || testVideos.pushup;

    // Auto-play videos when they load
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.play().catch(() => {
                // Auto-play failed, user will need to click play
            });
        }
        if (modVideoRef.current) {
            modVideoRef.current.play().catch(() => {
                // Auto-play failed, user will need to click play
            });
        }
    }, [phase, showModification]);

    // Countdown timer effect
    useEffect(() => {
        if (phase === 'countdown' && countdown > 0) {
            countdownRef.current = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
        } else if (phase === 'countdown' && countdown === 0) {
            setPhase('testing');
            setIsActive(true);
            setTimeLeft(currentTest.duration);
        }

        return () => {
            if (countdownRef.current) {
                clearTimeout(countdownRef.current);
            }
        };
    }, [phase, countdown, currentTest.duration]);

    // Test timer effect
    useEffect(() => {
        if (phase === 'testing' && isActive && timeLeft > 0) {
            intervalRef.current = setInterval(() => {
                setTimeLeft(time => {
                    if (time <= 1) {
                        setIsActive(false);
                        setPhase('results');
                        return 0;
                    }
                    return time - 1;
                });
            }, 1000);
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [phase, isActive, timeLeft]);

    const handleSetupSubmit = (e) => {
        e.preventDefault();
        if (userAge && userGender && (currentTest.isWeightBased ? userWeight : true)) {
            setPhase('instructions');
        }
    };

    const handleStartTest = () => {
        setPhase('countdown');
        setCountdown(5);
    };

    const handleFinishEarly = () => {
        setIsActive(false);
        setPhase('results');
    };

    const handleRepIncrement = () => {
        setReps(reps + 1);
    };

    const handleRepDecrement = () => {
        if (reps > 0) {
            setReps(reps - 1);
        }
    };

    const handleWeightIncrement = (amount) => {
        setWeight(weight + amount);
    };

    const handleWeightDecrement = (amount) => {
        if (weight >= amount) {
            setWeight(weight - amount);
        }
    };

    const handleComplete = () => {
        setPhase('final-results');
    };

    const handleFinalComplete = () => {
        // For weight-based tests, pass the weight lifted, otherwise pass reps
        const score = currentTest.isWeightBased ? weight : reps;
        onComplete(testType, score, parseInt(userAge), userGender, currentTest.isWeightBased ? parseInt(userWeight) : null);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getPerformanceMessage = () => {
        const score = currentTest.isWeightBased ? weight : reps;

        if (currentTest.isWeightBased) {
            if (score >= userWeight * 1.5) return "🔥 LEGENDARY STRENGTH!";
            if (score >= userWeight * 1.2) return "💪 EXCELLENT POWER!";
            if (score >= userWeight * 0.9) return "⚡ SOLID EFFORT!";
            if (score >= userWeight * 0.5) return "🌟 GOOD START!";
            return "💯 BUILDING STRENGTH!";
        } else {
            if (testType === 'pushup') {
                if (reps >= 50) return "🔥 LEGENDARY PERFORMANCE!";
                if (reps >= 30) return "💪 EXCELLENT WORK!";
                if (reps >= 15) return "⚡ SOLID EFFORT!";
                if (reps >= 5) return "🌟 GOOD START!";
                return "💯 EVERY REP COUNTS!";
            }
            if (testType === 'pullup') {
                if (reps >= 20) return "🔥 ELITE LEVEL!";
                if (reps >= 10) return "💪 OUTSTANDING!";
                if (reps >= 5) return "⚡ IMPRESSIVE!";
                if (reps >= 1) return "🌟 GREAT JOB!";
                return "💯 BUILDING STRENGTH!";
            }
            return "🌟 WELL DONE!";
        }
    };

    if (phase === 'setup') {
        return (
            <div className="fitness-test-widget">
                <div className="test-setup">
                    <h2>🏋️ {currentTest.name}</h2>
                    <p>To provide accurate performance rankings, we need some basic information:</p>

                    <form onSubmit={handleSetupSubmit} className="setup-form">
                        <div className="form-group">
                            <label htmlFor="age">Age:</label>
                            <input
                                type="number"
                                id="age"
                                min="13"
                                max="100"
                                value={userAge}
                                onChange={(e) => setUserAge(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="gender">Gender:</label>
                            <select
                                id="gender"
                                value={userGender}
                                onChange={(e) => setUserGender(e.target.value)}
                                required
                            >
                                <option value="">Select...</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                            </select>
                        </div>

                        {currentTest.isWeightBased && (
                            <div className="form-group">
                                <label htmlFor="weight">Your Body Weight (lbs):</label>
                                <input
                                    type="number"
                                    id="weight"
                                    min="80"
                                    max="400"
                                    value={userWeight}
                                    onChange={(e) => setUserWeight(e.target.value)}
                                    required
                                    placeholder="e.g., 150"
                                />
                                <small>Used to calculate strength ratios</small>
                            </div>
                        )}

                        <div className="form-buttons">
                            <button type="button" onClick={onCancel} className="cancel-btn">
                                Cancel
                            </button>
                            <button type="submit" className="continue-btn">
                                Continue
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    if (phase === 'instructions') {
        return (
            <div className="fitness-test-widget">
                <div className="test-instructions">
                    <h2>📋 {currentTest.name} Instructions</h2>

                    <div className="instruction-content-grid">
                        <div className="video-section">
                            <div className="primary-video">
                                <h3>🎯 Proper Form</h3>
                                {currentVideos.primary && (
                                    <video
                                        ref={videoRef}
                                        src={currentVideos.primary}
                                        loop
                                        muted
                                        controls
                                        className="instruction-video"
                                        onLoadedData={() => {
                                            if (videoRef.current) {
                                                videoRef.current.play().catch(() => {});
                                            }
                                        }}
                                    />
                                )}
                            </div>

                            {currentVideos.modification && (
                                <div className="modification-section">
                                    <div className="modification-toggle">
                                        <button
                                            className={`modification-btn ${showModification ? 'active' : ''}`}
                                            onClick={() => setShowModification(!showModification)}
                                        >
                                            {showModification ? 'Hide' : 'Show'} Modification: {currentVideos.modificationName}
                                        </button>
                                    </div>

                                    {showModification && (
                                        <div className="modification-video">
                                            <h4>🔄 Easier Alternative</h4>
                                            <video
                                                ref={modVideoRef}
                                                src={currentVideos.modification}
                                                loop
                                                muted
                                                controls
                                                className="instruction-video modification"
                                                onLoadedData={() => {
                                                    if (modVideoRef.current) {
                                                        modVideoRef.current.play().catch(() => {});
                                                    }
                                                }}
                                            />
                                            <p className="modification-note">
                                                💡 If the main exercise is too challenging, you can perform this modification instead and still get great results!
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="instructions-text">
                            <div className="instructions-list">
                                {currentTest.instructions.map((instruction, index) => (
                                    <div key={index} className="instruction-item">
                                        <span className="instruction-number">{index + 1}</span>
                                        <span className="instruction-text">{instruction}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="test-tips">
                                <h3>💡 Pro Tip:</h3>
                                <p>{currentTest.tips}</p>
                            </div>

                            <div className="test-info">
                                <p><strong>Duration:</strong> {formatTime(currentTest.duration)} max</p>
                                <p><strong>Goal:</strong> {currentTest.isWeightBased ? 'Find your maximum weight' : 'Complete as many quality repetitions as possible'}</p>
                                {currentTest.isWeightBased && (
                                    <p><strong>Your Weight:</strong> {userWeight} lbs</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="instruction-buttons">
                        <button onClick={() => setPhase('setup')} className="back-btn">
                            ← Back
                        </button>
                        <button onClick={handleStartTest} className="start-btn">
                            Start Test
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (phase === 'countdown') {
        return (
            <div className="fitness-test-widget">
                <div className="test-countdown">
                    <h2>🚀 Get Ready!</h2>
                    <div className="countdown-circle">
                        <span className="countdown-number">{countdown}</span>
                    </div>
                    <p>Position yourself and prepare to begin the {currentTest.name}</p>
                    {currentTest.isWeightBased && (
                        <p className="weight-reminder">⚠️ Ensure you have a spotter and proper safety equipment!</p>
                    )}
                </div>
            </div>
        );
    }

    if (phase === 'testing') {
        return (
            <div className="fitness-test-widget">
                <div className="test-active">
                    <div className="test-header">
                        <h2>🔥 {currentTest.name} IN PROGRESS</h2>
                        <div className="timer">
                            <span className="time-label">Time Remaining:</span>
                            <span className="time-value">{formatTime(timeLeft)}</span>
                        </div>
                    </div>

                    <div className="test-in-progress">
                        <div className="motivational-display">
                            <div className="big-timer">
                                <span className="timer-number">{formatTime(timeLeft)}</span>
                                <span className="timer-label">
                                    {currentTest.isWeightBased ? 'FIND YOUR MAX!' : 'KEEP GOING!'}
                                </span>
                            </div>
                        </div>

                        <div className="testing-video-reminder">
                            {currentVideos.primary && (
                                <div className="form-reminder-video">
                                    <h4>🎯 Keep This Form!</h4>
                                    <video
                                        src={currentVideos.primary}
                                        loop
                                        muted
                                        autoPlay
                                        className="reminder-video"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="test-instructions-reminder">
                            <h3>📋 Remember:</h3>
                            <ul>
                                {currentTest.instructions.slice(0, 3).map((instruction, index) => (
                                    <li key={index}>{instruction}</li>
                                ))}
                            </ul>
                        </div>

                        <div className="test-controls">
                            <button onClick={handleFinishEarly} className="finish-btn">
                                Finish Test
                            </button>
                        </div>

                        <div className="motivation-text">
                            <p>💪 Focus on your form!</p>
                            {currentTest.isWeightBased ? (
                                <>
                                    <p>🏋️ Find your maximum safely!</p>
                                    <p>⚡ Quality over quantity!</p>
                                </>
                            ) : (
                                <>
                                    <p>🔥 Count your reps mentally!</p>
                                    <p>⚡ Push through the burn!</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (phase === 'results') {
        return (
            <div className="fitness-test-widget">
                <div className="test-results">
                    <h2>⏰ Test Complete!</h2>

                    <div className="input-section">
                        <h3>📊 Enter your {currentTest.isWeightBased ? 'maximum weight lifted' : 'total repetitions completed'}:</h3>

                        {currentTest.isWeightBased ? (
                            <div className="weight-input-controls">
                                <button
                                    onClick={() => handleWeightDecrement(10)}
                                    className="weight-btn decrement"
                                    disabled={weight < 10}
                                >
                                    -10
                                </button>
                                <button
                                    onClick={() => handleWeightDecrement(5)}
                                    className="weight-btn decrement"
                                    disabled={weight < 5}
                                >
                                    -5
                                </button>

                                <div className="weight-display">
                                    <span className="weight-number">{weight}</span>
                                    <span className="weight-label">LBS</span>
                                </div>

                                <button
                                    onClick={() => handleWeightIncrement(5)}
                                    className="weight-btn increment"
                                >
                                    +5
                                </button>
                                <button
                                    onClick={() => handleWeightIncrement(10)}
                                    className="weight-btn increment"
                                >
                                    +10
                                </button>
                            </div>
                        ) : (
                            <div className="rep-input-controls">
                                <button
                                    onClick={handleRepDecrement}
                                    className="rep-btn decrement"
                                    disabled={reps === 0}
                                >
                                    -1
                                </button>

                                <div className="rep-display">
                                    <span className="rep-number">{reps}</span>
                                    <span className="rep-label">REPS</span>
                                </div>

                                <button
                                    onClick={handleRepIncrement}
                                    className="rep-btn increment"
                                >
                                    +1
                                </button>
                            </div>
                        )}

                        <div className="quick-select">
                            <p>Quick select:</p>
                            <div className="quick-buttons">
                                {currentTest.isWeightBased ? (
                                    [0, 95, 135, 185, 225, 275, 315, 365, 405].map(num => (
                                        <button
                                            key={num}
                                            onClick={() => setWeight(num)}
                                            className="quick-btn"
                                        >
                                            {num}
                                        </button>
                                    ))
                                ) : (
                                    [0, 5, 10, 15, 20, 25, 30, 40, 50].map(num => (
                                        <button
                                            key={num}
                                            onClick={() => setReps(num)}
                                            className="quick-btn"
                                        >
                                            {num}
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="test-details">
                        <p><strong>Test:</strong> {currentTest.name}</p>
                        <p><strong>Age:</strong> {userAge}</p>
                        <p><strong>Gender:</strong> {userGender}</p>
                        {currentTest.isWeightBased && <p><strong>Body Weight:</strong> {userWeight} lbs</p>}
                        <p><strong>Duration:</strong> {formatTime(currentTest.duration - timeLeft)}</p>
                    </div>

                    <div className="results-buttons">
                        <button onClick={() => {
                            setPhase('setup');
                            setReps(0);
                            setWeight(0);
                            setTimeLeft(currentTest.duration);
                        }} className="retry-btn">
                            Retry Test
                        </button>
                        <button
                            onClick={handleComplete}
                            className="continue-btn"
                        >
                            Continue Story ({currentTest.isWeightBased ? `${weight} lbs` : `${reps} reps`})
                        </button>
                    </div>

                    <div className="honesty-reminder">
                        <p>🏆 <strong>Be honest with your {currentTest.isWeightBased ? 'weight' : 'count'}!</strong></p>
                        <p>Your progress depends on accurate self-reporting. Every warrior builds on truth!</p>
                    </div>
                </div>
            </div>
        );
    }

    if (phase === 'final-results') {
        const finalScore = currentTest.isWeightBased ? weight : reps;
        const scoreLabel = currentTest.isWeightBased ? 'lbs Lifted' : `${currentTest.name.split(' ')[0]}s Completed`;

        return (
            <div className="fitness-test-widget">
                <div className="test-results">
                    <h2>🏆 Test Complete!</h2>

                    <div className="results-summary">
                        <div className="final-score">
                            <span className="score-number">{finalScore}</span>
                            <span className="score-label">{scoreLabel}</span>
                        </div>

                        <div className="performance-message">
                            <p>{getPerformanceMessage()}</p>
                        </div>

                        <div className="test-details">
                            <p><strong>Test:</strong> {currentTest.name}</p>
                            <p><strong>Age:</strong> {userAge}</p>
                            <p><strong>Gender:</strong> {userGender}</p>
                            {currentTest.isWeightBased && <p><strong>Body Weight:</strong> {userWeight} lbs</p>}
                            <p><strong>Score:</strong> {finalScore} {currentTest.isWeightBased ? 'lbs' : 'reps'}</p>
                            <p><strong>Duration:</strong> {formatTime(currentTest.duration - timeLeft)}</p>
                            {currentTest.isWeightBased && userWeight && (
                                <p><strong>Ratio:</strong> {(finalScore / userWeight).toFixed(2)}x bodyweight</p>
                            )}
                        </div>
                    </div>

                    <div className="results-buttons">
                        <button onClick={() => {
                            setPhase('setup');
                            setReps(0);
                            setWeight(0);
                            setTimeLeft(currentTest.duration);
                        }} className="retry-btn">
                            Retry Test
                        </button>
                        <button onClick={handleFinalComplete} className="continue-btn">
                            Continue Story
                        </button>
                    </div>

                    <div className="next-steps">
                        <p>🎯 Your performance will influence your story path and unlock new characters!</p>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default FitnessTestWidget;